import { useState, type FormEvent } from 'react';
import { useIntl } from 'react-intl';
import { Link, Outlet, useLoaderData, useLocation, type LoaderFunctionArgs } from 'react-router-dom';
import { Button, InfoBox, Input, Label, Modal, Checkbox, NativeSelect } from '@immo/ui-kit';
import {
  WHATSAPP_OPERATOR_LIST_MAX_LIMIT,
  type AdminOrganizationListItem,
  type AdminPortalConnectionResponse,
  type CreateAdminWorkspaceInvitationBody,
  type GetAdminWorkspaceResponse,
  type ListBuyerContractTemplatesResponse,
  type ListEmailTemplatesResponse,
  type OrgRoleSlug,
  type PendingInvitation,
  type WhatsAppFailedDispatch,
  type WhatsAppUnmatchedMessage,
  type WorkspaceRoleSlug,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { requireUuidParam } from '@/lib/routing/require-uuid-param';
import { useTranslations } from '@/i18n/use-translations';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { WorkspacesIcon } from '@/components/layout/icons';
import { ResourceHeader } from '@/components/resource-header';
import { TestBadge } from '@/components/test-workspaces';
import { BuyerContractSection } from '@/pages/workspaces/buyer-contract-section';
import { EmailTemplatesSection } from '@/pages/workspaces/email-templates-section';
import { PortalConnectionsSection } from '@/pages/workspaces/portal-connections-section';
import { WhatsAppSection, type WhatsAppList } from '@/pages/workspaces/whatsapp-section';
import { ALL_WORKSPACES_QUERY, useIncludeTestWorkspaces } from '@/lib/test-workspaces/include-test-workspaces';

const FIRST_ADMIN_ROLES: ReadonlySet<WorkspaceRoleSlug> = new Set(['workspace-owner', 'workspace-admin', 'hlre-admin']);

type WorkspaceDetailData = {
  readonly workspace: GetAdminWorkspaceResponse;
  readonly pending: ReadonlyArray<PendingInvitation>;
  readonly organizations: ReadonlyArray<AdminOrganizationListItem>;
  readonly failedDispatches: WhatsAppList<WhatsAppFailedDispatch>;
  readonly unmatchedMessages: WhatsAppList<WhatsAppUnmatchedMessage>;
  readonly portalConnections: ReadonlyArray<AdminPortalConnectionResponse> | null;
  readonly emailTemplates: ListEmailTemplatesResponse['data'] | null;
  readonly buyerContractTemplates: ListBuyerContractTemplatesResponse['data'] | null;
};

export async function workspaceDetailLoader({ params }: LoaderFunctionArgs): Promise<WorkspaceDetailData> {
  // A non-uuid `:id` (e.g. the retired `/workspaces/api-calls` address, which
  // binds this route) is a wrong address, not a fault: refuse it with a 404
  // here so the operator sees NotFound, before the API turns it into a 400 the
  // error boundary reports as an incident (IMM-513).
  const id = requireUuidParam(params, 'id');

  const [
    workspaceResult,
    pendingResult,
    organizationsResult,
    failedResult,
    unmatchedResult,
    portalResult,
    emailTemplatesResult,
    buyerContractTemplatesResult,
  ] = await Promise.all([
    apiClient.adminWorkspaces.get({ pathParams: { id } }),
    // Supplementary — neither a non-OK response nor a rejected request (network,
    // CORS, fetch throwing) may blank the detail route; only the pending panel
    // degrades. The `.catch` keeps a rejection out of the `Promise.all`.
    apiClient.adminWorkspaces.listPendingInvitations({ pathParams: { workspaceId: id } }).catch(() => null),
    // Supplementary — backs the invite form's Standort picker. Scoped to this
    // workspace server-side so the page does bounded work as the platform grows.
    // A failure leaves the picker empty rather than blanking the route. Asked
    // for test workspaces explicitly: a detail page is never gated by the
    // operator's the test-workspace filter filter (IMM-593.2), and this workspace may
    // well be one.
    apiClient.adminOrganizations.list({ query: { workspaceId: id, ...ALL_WORKSPACES_QUERY } }).catch(() => null),
    // Supplementary — each WhatsApp list degrades only its own panel. A failed
    // read resolves to `null` below, distinct from an empty page (BL-058).
    apiClient.adminWhatsApp
      .listFailedDispatches({
        pathParams: { workspaceId: id },
        query: { limit: WHATSAPP_OPERATOR_LIST_MAX_LIMIT },
      })
      .catch(() => null),
    apiClient.adminWhatsApp
      .listUnmatchedMessages({ query: { limit: WHATSAPP_OPERATOR_LIST_MAX_LIMIT } })
      .catch(() => null),
    // Supplementary — a failed read degrades only the portal section; `null`
    // below is distinct from a real NOT_CONNECTED answer (BL-058).
    apiClient.adminPortalConnections.list({ pathParams: { workspaceId: id } }).catch(() => null),
    // Supplementary — a failed read degrades only the E-Mail-Vorlagen section;
    // `null` below is distinct from an empty list (BL-058).
    apiClient.adminEmailTemplates.list({ pathParams: { workspaceId: id } }).catch(() => null),
    // Supplementary — a failed read degrades only the Maklervertrag Käufer section;
    // `null` below is distinct from a workspace on the standard (BL-058).
    apiClient.adminBuyerContractTemplates.list({ pathParams: { workspaceId: id } }).catch(() => null),
  ]);

  if (!workspaceResult.ok) {
    throw new Response('Failed to load workspace', { status: workspaceResult.status });
  }

  const organizations = organizationsResult?.ok ? organizationsResult.body.data : [];

  return {
    workspace: workspaceResult.body,
    pending: pendingResult?.ok ? pendingResult.body.data : [],
    organizations,
    failedDispatches: failedResult?.ok
      ? { items: failedResult.body.data, truncated: failedResult.body.truncated }
      : null,
    unmatchedMessages: unmatchedResult?.ok
      ? { items: unmatchedResult.body.data, truncated: unmatchedResult.body.truncated }
      : null,
    portalConnections: portalResult?.ok ? portalResult.body.connections : null,
    emailTemplates: emailTemplatesResult?.ok ? emailTemplatesResult.body.data : null,
    buyerContractTemplates: buyerContractTemplatesResult?.ok ? buyerContractTemplatesResult.body.data : null,
  };
}

function formatCoordinates(latitude: number | null, longitude: number | null): string | null {
  if (latitude === null || longitude === null) return null;

  return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
}

function formatAddressLine(address: GetAdminWorkspaceResponse['address']): string {
  const street = [address.street, address.houseNumber]
    .filter((p) => p !== '')
    .join(' ')
    .trim();
  const city = [address.postalCode, address.city]
    .filter((p) => p !== '')
    .join(' ')
    .trim();

  return [street, city].filter((p) => p !== '').join(', ');
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{children}</h2>;
}

function DefRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-text-tertiary">{label}</dt>
      <dd className="text-text-primary">{children}</dd>
    </>
  );
}

function InviteMemberSection({
  workspaceId,
  organizations,
  offeredRoles,
  onInvited,
}: {
  readonly workspaceId: string;
  readonly organizations: ReadonlyArray<AdminOrganizationListItem>;
  readonly offeredRoles: GetAdminWorkspaceResponse['offeredRoles'];
  readonly onInvited: (invitation: PendingInvitation) => void;
}) {
  const t = useTranslations('pages.admin.workspaces.detail');
  // A workspace without Standort roles (HLRE) invites with a workspace role alone.
  const standortRoles = offeredRoles.organisationRoles.length > 0;
  // The operator invites such a workspace's first admin; its colleagues are invited in the app.
  const workspaceRoleChoices = standortRoles
    ? offeredRoles.workspaceRoles
    : offeredRoles.workspaceRoles.filter((role) => FIRST_ADMIN_ROLES.has(role));
  const defaultWorkspaceRole: WorkspaceRoleSlug | '' = standortRoles ? '' : (workspaceRoleChoices[0] ?? '');
  const mainOrgId = organizations.find((organization) => organization.type === 'main')?.id ?? null;
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organisationRole, setOrganisationRole] = useState<OrgRoleSlug>('agent');
  const [workspaceRole, setWorkspaceRole] = useState<WorkspaceRoleSlug | ''>(defaultWorkspaceRole);
  const [selectedOrgIds, setSelectedOrgIds] = useState<ReadonlyArray<string>>(mainOrgId !== null ? [mainOrgId] : []);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [open, setOpen] = useState(false);

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setMessage(null);
  };

  const toggleOrg = (id: string) =>
    setSelectedOrgIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

  const resetForm = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setOrganisationRole('agent');
    setWorkspaceRole(defaultWorkspaceRole);
    setSelectedOrgIds(mainOrgId !== null ? [mainOrgId] : []);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage(null);

    if (
      email.trim().length === 0 ||
      firstName.trim().length === 0 ||
      lastName.trim().length === 0 ||
      (standortRoles && selectedOrgIds.length === 0)
    ) {
      setMessage({ tone: 'error', text: t('invite-validation-error') });

      return;
    }

    const primaryOrganizationId =
      mainOrgId !== null && selectedOrgIds.includes(mainOrgId) ? mainOrgId : (selectedOrgIds[0] ?? null);

    const identity = { email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim() };
    const body: CreateAdminWorkspaceInvitationBody = standortRoles
      ? {
          ...identity,
          organisationRole,
          organizationIds: [...selectedOrgIds],
          primaryOrganizationId,
          ...(workspaceRole !== '' ? { workspaceRole } : {}),
        }
      : {
          ...identity,
          organizationIds: [],
          primaryOrganizationId: null,
          ...(workspaceRole !== '' ? { workspaceRole } : {}),
        };

    setSubmitting(true);

    try {
      const result = await apiClient.adminWorkspaces.createInvitation({ pathParams: { workspaceId }, body });

      if (!result.ok) {
        setMessage({ tone: 'error', text: t('invite-error') });

        return;
      }

      onInvited(result.body);
      resetForm();
      setOpen(false);
    } catch {
      setMessage({ tone: 'error', text: t('invite-error') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="space-y-3">
      <Button size="sm" type="button" onClick={() => setOpen(true)}>
        {t('invite-heading')}
      </Button>

      <Modal
        open={open}
        onClose={close}
        size="lg"
        title={t('invite-heading')}
        footer={
          <>
            <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
              {t('invite-cancel-cta')}
            </Button>
            <Button type="submit" form="invite-member-form" disabled={submitting}>
              {submitting ? t('invite-submitting') : t('invite-submit-cta')}
            </Button>
          </>
        }
      >
        <form id="invite-member-form" className="space-y-4" onSubmit={submit} noValidate>
          <div className="space-y-1">
            <Label htmlFor="invite-email">{t('invite-email-label')}</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('invite-email-placeholder')}
              disabled={submitting}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="invite-first-name">{t('invite-first-name-label')}</Label>
              <Input
                id="invite-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-last-name">{t('invite-last-name-label')}</Label>
              <Input
                id="invite-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {standortRoles && (
              <div className="space-y-1">
                <Label htmlFor="invite-organisation-role">{t('invite-organisation-role-label')}</Label>
                <NativeSelect
                  id="invite-organisation-role"
                  value={organisationRole}
                  onChange={(value) => setOrganisationRole(value as OrgRoleSlug)}
                  disabled={submitting}
                  options={offeredRoles.organisationRoles.map((role) => ({
                    value: role,
                    label: t(`org-role.${role}`),
                  }))}
                />
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="invite-workspace-role">{t('invite-workspace-role-label')}</Label>
              <NativeSelect
                id="invite-workspace-role"
                value={workspaceRole}
                onChange={(value) => setWorkspaceRole(value as WorkspaceRoleSlug | '')}
                disabled={submitting}
                options={[
                  ...(standortRoles ? [{ value: '', label: t('invite-workspace-role-none') }] : []),
                  ...workspaceRoleChoices.map((role) => ({ value: role, label: t(`workspace-role.${role}`) })),
                ]}
              />
            </div>
          </div>

          {standortRoles && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-text-primary">{t('invite-standorte-label')}</legend>
              {organizations.length === 0 ? (
                <p className="text-text-tertiary">{t('invite-standorte-empty')}</p>
              ) : (
                <div className="space-y-2">
                  {organizations.map((organization) => (
                    <label key={organization.id} className="flex items-center gap-2 text-sm text-text-primary">
                      <Checkbox
                        checked={selectedOrgIds.includes(organization.id)}
                        disabled={submitting}
                        onCheckedChange={() => toggleOrg(organization.id)}
                      />
                      {organization.name}
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
          )}

          {message !== null && message.tone === 'error' && <p className="text-sm text-danger-600">{message.text}</p>}
        </form>
      </Modal>
    </section>
  );
}

function PendingMembersSection({
  workspaceId,
  items,
  setItems,
}: {
  readonly workspaceId: string;
  readonly items: ReadonlyArray<PendingInvitation>;
  readonly setItems: React.Dispatch<React.SetStateAction<ReadonlyArray<PendingInvitation>>>;
}) {
  const t = useTranslations('pages.admin.workspaces.detail');
  const intl = useIntl();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const resend = (id: string) => async () => {
    setBusyId(id);
    setMessage(null);

    try {
      const result = await apiClient.adminWorkspaces.resendInvitation({ pathParams: { workspaceId, id }, body: {} });

      if (!result.ok) {
        setMessage({ tone: 'error', text: t('pending-resend-error') });

        return;
      }

      // Resend revokes the old record and returns the fresh invitation (new id).
      // Swap the row in place from the response so state is never left holding the
      // revoked id, even if the follow-up list refresh fails. Then re-read the
      // list best-effort for full consistency, but don't gate success on it.
      const fresh = result.body;

      setItems((current) => current.map((invitation) => (invitation.id === id ? fresh : invitation)));

      const refreshed = await apiClient.adminWorkspaces
        .listPendingInvitations({ pathParams: { workspaceId } })
        .catch(() => null);

      if (refreshed?.ok) setItems(refreshed.body.data);

      setMessage({ tone: 'ok', text: t('pending-resent') });
    } catch {
      setMessage({ tone: 'error', text: t('pending-resend-error') });
    } finally {
      setBusyId(null);
    }
  };

  const revoke = (id: string) => async () => {
    setBusyId(id);
    setMessage(null);

    try {
      const result = await apiClient.adminWorkspaces.revokeInvitation({ pathParams: { workspaceId, id }, body: {} });

      if (!result.ok) {
        setMessage({ tone: 'error', text: t('pending-revoke-error') });

        return;
      }

      setItems((current) => current.filter((invitation) => invitation.id !== id));
    } catch {
      setMessage({ tone: 'error', text: t('pending-revoke-error') });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{t('pending-heading')}</h2>

      {items.length === 0 ? (
        <p className="text-text-tertiary">{t('pending-empty')}</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {items.map((invitation) => (
            <li key={invitation.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-text-primary">{invitation.email}</p>
                <p className="text-sm text-text-tertiary">
                  {t('pending-expires-label')}: {intl.formatDate(invitation.expiresAt, { dateStyle: 'medium' })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" disabled={busyId !== null} onClick={resend(invitation.id)}>
                  {busyId === invitation.id ? t('pending-resending') : t('pending-resend-cta')}
                </Button>
                <Button size="sm" variant="ghost" disabled={busyId !== null} onClick={revoke(invitation.id)}>
                  {busyId === invitation.id ? t('pending-revoking') : t('pending-revoke-cta')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {message !== null && (
        <p className={message.tone === 'error' ? 'text-sm text-danger-600' : 'text-sm text-text-secondary'}>
          {message.text}
        </p>
      )}
    </section>
  );
}

function MembersPanel({
  workspaceId,
  initial,
  organizations,
  offeredRoles,
}: {
  readonly workspaceId: string;
  readonly initial: ReadonlyArray<PendingInvitation>;
  readonly organizations: ReadonlyArray<AdminOrganizationListItem>;
  readonly offeredRoles: GetAdminWorkspaceResponse['offeredRoles'];
}) {
  const [items, setItems] = useState<ReadonlyArray<PendingInvitation>>(initial);

  const prepend = (invitation: PendingInvitation) =>
    setItems((current) => [invitation, ...current.filter((existing) => existing.id !== invitation.id)]);

  return (
    <>
      <InviteMemberSection
        workspaceId={workspaceId}
        organizations={organizations}
        offeredRoles={offeredRoles}
        onInvited={prepend}
      />
      <PendingMembersSection workspaceId={workspaceId} items={items} setItems={setItems} />
    </>
  );
}

export function WorkspaceDetailPage() {
  const t = useTranslations('pages.admin.workspaces.detail');
  const tList = useTranslations('pages.admin.workspaces');
  const intl = useIntl();
  const {
    workspace,
    pending,
    organizations,
    failedDispatches,
    unmatchedMessages,
    portalConnections,
    emailTemplates,
    buyerContractTemplates,
  } = useLoaderData() as WorkspaceDetailData;
  const location = useLocation();
  const includeTestWorkspaces = useIncludeTestWorkspaces();
  // Set by the create form's navigation (IMM-593.2): a freshly created
  // test workspace is invisible in the lists while the filter is inactive, so say
  // so — but only while it is actually hidden.
  const createdAsTest = (location.state as { createdAsTest?: boolean } | null)?.createdAsTest === true;
  const showCreatedTestNote = createdAsTest && workspace.isTest && !includeTestWorkspaces;
  const ownerName = workspace.owner === null ? '' : `${workspace.owner.firstName} ${workspace.owner.lastName}`.trim();
  const coordinates = formatCoordinates(workspace.address.latitude, workspace.address.longitude);
  const addressLine = formatAddressLine(workspace.address);

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={WorkspacesIcon}
            crumbs={[{ label: tList('title'), to: '/workspaces' }, { label: workspace.name }]}
          />
        }
        trailing={
          <Button size="sm" asChild>
            <Link to={`/workspaces/${workspace.id}/edit`}>{t('edit-cta')}</Link>
          </Button>
        }
      />
      <ResourceHeader
        icon={WorkspacesIcon}
        title={workspace.name}
        meta={
          <span className="inline-flex items-center gap-2">
            {addressLine === '' ? workspace.organization.name : addressLine}
            <TestBadge isTest={workspace.isTest} />
          </span>
        }
        status={{ tone: 'aktiv', label: t('status-aktiv') }}
      />
      <div className="space-y-8 px-6 pb-8">
        {showCreatedTestNote && <InfoBox type="warning">{t('created-test-hidden')}</InfoBox>}
        <section className="space-y-2">
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <DefRow label={t('id-label')}>
              <span className="font-mono text-sm">{workspace.id}</span>
            </DefRow>
            <DefRow label={t('created-at-label')}>{intl.formatDate(workspace.createdAt, { dateStyle: 'long' })}</DefRow>
          </dl>
        </section>

        <section className="space-y-2">
          <SectionHeading>{t('owner-heading')}</SectionHeading>
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <DefRow label={t('owner-name-label')}>{ownerName === '' ? '—' : ownerName}</DefRow>
            <DefRow label={t('owner-email-label')}>{workspace.owner?.email ?? '—'}</DefRow>
          </dl>
        </section>

        <MembersPanel
          workspaceId={workspace.id}
          initial={pending}
          organizations={organizations ?? []}
          offeredRoles={workspace.offeredRoles}
        />

        <WhatsAppSection failed={failedDispatches} unmatched={unmatchedMessages} />
        <PortalConnectionsSection connections={portalConnections} />
        <EmailTemplatesSection workspaceId={workspace.id} templates={emailTemplates} />
        <BuyerContractSection templates={buyerContractTemplates} />

        <section className="space-y-2">
          <SectionHeading>{t('organization-heading')}</SectionHeading>
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <DefRow label={t('organization-name-label')}>{workspace.organization.name}</DefRow>
          </dl>
        </section>

        <section className="space-y-2">
          <SectionHeading>{t('address-heading')}</SectionHeading>
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <DefRow label={t('street-label')}>{workspace.address.street}</DefRow>
            <DefRow label={t('house-number-label')}>{workspace.address.houseNumber}</DefRow>
            <DefRow label={t('postal-code-label')}>{workspace.address.postalCode}</DefRow>
            <DefRow label={t('city-label')}>{workspace.address.city}</DefRow>
            <DefRow label={t('country-label')}>{workspace.address.country}</DefRow>
            {coordinates !== null && (
              <DefRow label={t('coordinates-label')}>
                <span className="font-mono text-sm">{coordinates}</span>
              </DefRow>
            )}
          </dl>
        </section>
      </div>
      <Outlet />
    </>
  );
}
